import { createSignal, onCleanup } from "solid-js";
import type { StatusResponse, LogEntry } from "./types";

const MAX_RETRIES = 8;
const BASE_DELAY_MS = 2000;

export function useWS(url: string) {
  const [status, setStatus] = createSignal<StatusResponse | null>(null);
  const [connected, setConnected] = createSignal(false);
  const [logs, setLogs] = createSignal<Record<string, LogEntry[]>>({});
  let ws: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout>;
  let retries = 0;

  function connect() {
    retries++;
    if (retries > MAX_RETRIES) {
      console.warn("[ws] max retries (%d) reached, giving up", MAX_RETRIES);
      return;
    }
    try {
      ws = new WebSocket(url);
      ws.onopen = () => {
        retries = 0;
        setConnected(true);
      };
      ws.onclose = () => {
        setConnected(false);
        ws = null;
        const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retries - 1), 30000);
        console.warn("[ws] disconnected, retry %d/%d in %dms", retries, MAX_RETRIES, delay);
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        console.error("[ws] connection error (attempt %d/%d, url: %s)", retries, MAX_RETRIES, url);
        ws?.close();
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "status" && msg.data) setStatus(msg.data);
          if (msg.type === "logs" && msg.server) {
            setLogs((p) => ({ ...p, [msg.server]: [...(p[msg.server] ?? []), ...msg.data] }));
          }
          if (msg.type === "logs-full" && msg.server) {
            setLogs((p) => ({ ...p, [msg.server]: msg.data }));
          }
        } catch {}
      };
    } catch {
      console.error("[ws] failed to create WebSocket (attempt %d/%d)", retries, MAX_RETRIES);
      const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retries - 1), 30000);
      reconnectTimer = setTimeout(connect, delay);
    }
  }

  connect();

  onCleanup(() => {
    clearTimeout(reconnectTimer);
    ws?.close();
  });

  function send(data: unknown) {
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(data));
  }

  return { status, connected, logs, send };
}
